import { Avatar, Card, CardProps, ColorPicker, Flex, Select, Space, Switch, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FormItem } from '../../components/form-item'
import { AvatarConfig } from '../input/update-input'

export type AvatarCustomizerProps = {
    value?: AvatarConfig | null
    onChange?: (config: AvatarConfig | null) => void
    userId: string
} & CardProps

const avatarOptions = {
    eyes: ['normal', 'confident', 'happy'],
    body: ['tShirt', 'golf'],
    head: ['normal', 'wide', 'thin'],
    hair: ['balndess', 'slaughter', 'ponyTail', 'long', 'curly', 'stylish', 'elvis', 'classic02', 'classic01'],
    mustache: ['none', 'pencilThinBeard', 'pencilThin', 'horshoe', 'freddy'],
} as const

function buildAvatarUrl(userId: string, config?: AvatarConfig | null): string {
    const baseUrl = 'https://api.dicebear.com/9.x/miniavs/svg'
    const params = new URLSearchParams()

    params.set('seed', userId)

    if (config) {
        Object.entries(config).forEach(([key, value]) => {
            if (value) {
                params.set(key, value)
            }
        })
    }

    return `${baseUrl}?${params.toString()}`
}

export function AvatarCustomizer({ value, onChange, userId, ...cardProps }: AvatarCustomizerProps) {
    const { t } = useTranslation()

    const eyesLabels = {
        normal: t('avatar.eyes.normal'),
        confident: t('avatar.eyes.confident'),
        happy: t('avatar.eyes.happy'),
    } as const

    const bodyLabels = {
        tShirt: t('avatar.body.tShirt'),
        golf: t('avatar.body.golf'),
    } as const

    const headLabels = {
        normal: t('avatar.head.normal'),
        wide: t('avatar.head.wide'),
        thin: t('avatar.head.thin'),
    } as const

    const hairLabels = {
        balndess: t('avatar.hair.balndess'),
        slaughter: t('avatar.hair.slaughter'),
        ponyTail: t('avatar.hair.ponyTail'),
        long: t('avatar.hair.long'),
        curly: t('avatar.hair.curly'),
        stylish: t('avatar.hair.stylish'),
        elvis: t('avatar.hair.elvis'),
        classic02: t('avatar.hair.classic02'),
        classic01: t('avatar.hair.classic01'),
    } as const

    const mustacheLabels = {
        none: t('avatar.mustache.none'),
        pencilThinBeard: t('avatar.mustache.pencilThinBeard'),
        pencilThin: t('avatar.mustache.pencilThin'),
        horshoe: t('avatar.mustache.horshoe'),
        freddy: t('avatar.mustache.freddy'),
    } as const

    const handleChange = (config: AvatarConfig) => {
        const newConfig = { ...value, ...config }
        onChange?.(newConfig)
    }

    const handleReset = () => {
        onChange?.(null)
    }

    return (
        <Card
            size="small"
            {...cardProps}
        >
            <Flex
                gap="large"
                className="flex-col md:flex-row"
            >
                <Flex
                    vertical
                    className="flex-1 min-w-0"
                >
                    <Flex gap="large">
                        <Flex
                            vertical
                            gap="small"
                            className="text-center"
                        >
                            <Avatar
                                size={120}
                                src={buildAvatarUrl(userId, value)}
                            />
                            <Typography.Link onClick={handleReset}>{t('Reset to default')}</Typography.Link>
                        </Flex>
                        <Flex vertical>
                            <Space>
                                <FormItem
                                    label={t('Hair Color')}
                                    className="flex-1"
                                >
                                    <ColorPicker
                                        value={value?.hairColor}
                                        onChange={(hairColor) =>
                                            handleChange({
                                                hairColor: hairColor.toHex(),
                                            })
                                        }
                                        allowClear
                                    />
                                </FormItem>

                                <FormItem
                                    label={t('Skin Color')}
                                    className="flex-1"
                                >
                                    <ColorPicker
                                        value={value?.skinColor}
                                        onChange={(skinColor) =>
                                            handleChange({
                                                skinColor: skinColor.toHex(),
                                            })
                                        }
                                        allowClear
                                    />
                                </FormItem>
                                <FormItem
                                    label={t('Shirt color')}
                                    className="flex-1"
                                >
                                    <ColorPicker
                                        value={value?.bodyColor}
                                        onChange={(bodyColor) =>
                                            handleChange({
                                                bodyColor: bodyColor.toHex(),
                                            })
                                        }
                                        allowClear
                                    />
                                </FormItem>
                            </Space>
                            <Flex justify="space-between">
                                <FormItem
                                    label={t('Glasses')}
                                    className="flex-1"
                                >
                                    <Switch
                                        value={value?.glasses === 'normal'}
                                        onChange={(glasses) =>
                                            handleChange({
                                                glasses: glasses ? 'normal' : undefined,
                                                glassesProbability: glasses ? '100' : '0',
                                            })
                                        }
                                    />
                                </FormItem>
                                <FormItem
                                    label={t('Blushes')}
                                    className="flex-1"
                                >
                                    <Switch
                                        value={value?.blushesProbability === '100'}
                                        onChange={(blushes) =>
                                            handleChange({
                                                blushesProbability: blushes ? '100' : '0',
                                            })
                                        }
                                    />
                                </FormItem>
                                <FormItem
                                    label={t('Tooth')}
                                    className="flex-1"
                                >
                                    <Switch
                                        value={value?.mouth === 'missingTooth'}
                                        onChange={(mouth) =>
                                            handleChange({
                                                mouth: mouth ? 'missingTooth' : 'default',
                                            })
                                        }
                                    />
                                </FormItem>
                            </Flex>
                        </Flex>
                    </Flex>

                    <FormItem
                        label={t('Eyes')}
                        className="flex-1"
                    >
                        <Select
                            value={value?.eyes}
                            onChange={(eyes) =>
                                handleChange({
                                    eyes,
                                })
                            }
                            placeholder={t('Default')}
                            options={avatarOptions.eyes.map((option) => ({
                                value: option,
                                label: eyesLabels[option],
                            }))}
                        />
                    </FormItem>

                    <FormItem
                        label={t('Head Shape')}
                        className="flex-1"
                    >
                        <Select
                            value={value?.head}
                            onChange={(head) =>
                                handleChange({
                                    head,
                                })
                            }
                            placeholder={t('Default')}
                            allowClear
                            options={avatarOptions.head.map((option) => ({
                                value: option,
                                label: headLabels[option],
                            }))}
                        />
                    </FormItem>

                    <FormItem
                        label={t('Hair Style')}
                        className="flex-1"
                    >
                        <Select
                            value={value?.hair}
                            onChange={(hair) =>
                                handleChange({
                                    hair,
                                })
                            }
                            placeholder={t('Default')}
                            allowClear
                            options={avatarOptions.hair.map((option) => ({
                                value: option,
                                label: hairLabels[option],
                            }))}
                        />
                    </FormItem>

                    <FormItem
                        label={t('Body Type')}
                        className="flex-1"
                    >
                        <Select
                            value={value?.body}
                            onChange={(body) =>
                                handleChange({
                                    body,
                                })
                            }
                            placeholder={t('Default')}
                            allowClear
                            options={avatarOptions.body.map((option) => ({
                                value: option,
                                label: bodyLabels[option],
                            }))}
                        />
                    </FormItem>
                    <FormItem
                        label={t('Mustache')}
                        className="flex-1"
                    >
                        <Select
                            value={value?.mustache}
                            onChange={(mustache) => {
                                handleChange({
                                    mustache: mustache === 'none' ? undefined : mustache,
                                    mustacheProbability: mustache === 'none' ? '0' : '100',
                                })
                            }}
                            placeholder={t('None')}
                            allowClear
                            options={avatarOptions.mustache.map((option) => ({
                                value: option,
                                label: mustacheLabels[option],
                            }))}
                        />
                    </FormItem>
                </Flex>
            </Flex>
        </Card>
    )
}
